import isBoolean from 'lodash/isBoolean';
import { isOverridingFragmentsAnalyticsPropEnabled } from '@helpcenter/feature-flags';
import { getMeta } from '@helpcenter/meta';
import { getAnalyticsWebClientPromise } from '@atlassian/help-center-common-util/analytics/client';
import { PACKAGE_NAME_GASV3 } from './constants';
import type {
	AnalyticsAttributes,
	EventInputProps,
	EventProps,
	InitializationProps,
	ScreenEventProps,
} from './types';
import { getErrorType } from './utils';

export const transformError = (error: Error) => ({
	...error,
	stack: error.stack,
	message: error?.message,
});

export const isSynthetic = () => getMeta('ajs-is-synthetic') === 'true';

export const isOverridingFragments = (): boolean => {
	const re = /^(local|PFE-BB-.*)$/;
	const buildKey = typeof window !== 'undefined' ? window?.BUILD_KEY : globalThis?.BUILD_KEY;
	return buildKey ? re.test(buildKey) : false;
};

export const createAttributes = (rawAttributes?: AnalyticsAttributes): AnalyticsAttributes => {
	const attributes: AnalyticsAttributes = {
		isSynthetic: isSynthetic(),
		...(isOverridingFragmentsAnalyticsPropEnabled() && {
			isOverridingFragments: isOverridingFragments(),
		}),
		isSSR: __SERVER__,
		shard: getMeta('ajs-shard') || '',
		region: getMeta('ajs-region') || '',
	};

	if (rawAttributes) {
		const { source, error, hasForgeAccess } = rawAttributes;

		if (source) {
			attributes.source = source;
		}

		if (error) {
			attributes.error = transformError(error);
			attributes.errorType = getErrorType(error);
		}

		if (isBoolean(hasForgeAccess)) {
			attributes.hasForgeAccess = hasForgeAccess;
		}
	}

	return { ...rawAttributes, ...attributes };
};

const createAnalyticsProps = ({ id, action, attributes, source }: EventProps) => ({
	source,
	action,
	actionSubject: `${PACKAGE_NAME_GASV3}.${id}`,
	attributes: createAttributes(attributes),
});

const createScreenAnalyticsProps = ({ id, attributes, source }: ScreenEventProps) => ({
	source,
	name: `${PACKAGE_NAME_GASV3}.${id}`,
	attributes: createAttributes(attributes),
});

const getAnalyticsClient = async () => (await getAnalyticsWebClientPromise()).getInstance();

const sendOperationalEvent = async (props: EventProps) =>
	(await getAnalyticsClient()).sendOperationalEvent(createAnalyticsProps(props));

const sendScreenEvent = async (props: ScreenEventProps) =>
	(await getAnalyticsClient()).sendScreenEvent(createScreenAnalyticsProps(props));

const sendTrackEvent = async (props: EventProps) =>
	(await getAnalyticsClient()).sendTrackEvent(createAnalyticsProps(props));

export const fireOperationalEvent = async (
	action: string,
	{ source, ...eventProps }: EventInputProps,
) =>
	sendOperationalEvent({
		...eventProps,
		source,
		action,
	});

export const fireScreenEvent = async (props: ScreenEventProps) => sendScreenEvent(props);

export const fireTrackEvent = async (action: string, { source, ...eventProps }: EventInputProps) =>
	sendTrackEvent({
		action,
		source,
		...eventProps,
	});

export const fireInitializationEvent = async (
	action: string,
	{ id, attributes, source }: InitializationProps,
) => {
	await fireOperationalEvent(action, {
		id,
		attributes,
		source,
	});
};
